import * as Yup from 'yup';
import { TFunction } from 'react-i18next';
import { BaseOption } from 'components/forms/multiSelectField/MultiSelectField.types';
import { DbsSettings, SocialPlatform } from 'models/DbsSettings';
import { SOCIAL_URL_MAX_LENGTH, isSupportedSocialUrl } from './socialUrl';

/** Matches the VARCHAR(255) tracking columns; the endpoint itself does not validate them. */
export const TRACKING_MAX_LENGTH = 255;
export const WIDGET_CTA_TITLE_MAX_LENGTH = 50;

export const TRACKING_FIELDS = [
  'googleAnalyticsId',
  'googleTagManagerId',
  'metaPixelId',
] as const;

export type TrackingField = (typeof TRACKING_FIELDS)[number];

export const SUPPORTED_SOCIAL_PLATFORMS = [
  SocialPlatform.FACEBOOK,
  SocialPlatform.INSTAGRAM,
  SocialPlatform.TWITTER,
  SocialPlatform.YOUTUBE,
] as const;

export type SupportedSocialPlatform =
  (typeof SUPPORTED_SOCIAL_PLATFORMS)[number];

export const WEBSITE_SETTINGS_TEXT_FIELDS = [
  'headline',
  'subHeadline',
  'welcomeTitle',
  'welcomeContent',
  'widgetCtaTitle',
] as const;

export type WebsiteSettingsTextField =
  (typeof WEBSITE_SETTINGS_TEXT_FIELDS)[number];

export type WebsiteSettingsFormValues = {
  [_key in WebsiteSettingsTextField]: string;
} & {
  featuredProperties: BaseOption[];
  socialLinks: Record<SupportedSocialPlatform, string>;
  tracking: Record<TrackingField, string>;
  widgetSettings: {
    requireGuestPhoneNumber: boolean;
    hidePriceWhenDateNotEntered: boolean;
  };
};

export const getFormDefaultValues = (
  dbsSettings?: DbsSettings,
  propertyOptions: BaseOption[] = [],
): WebsiteSettingsFormValues => ({
  headline: dbsSettings?.headline ?? '',
  subHeadline: dbsSettings?.subHeadline ?? '',
  welcomeTitle: dbsSettings?.welcomeTitle ?? '',
  welcomeContent: dbsSettings?.welcomeContent ?? '',
  widgetCtaTitle: dbsSettings?.widgetCtaTitle ?? '',
  featuredProperties: (dbsSettings?.featuredPropertyUids ?? [])
    .map((uid) => propertyOptions.find(({ value }) => value === uid))
    .filter((option): option is BaseOption => !!option),
  socialLinks: Object.fromEntries(
    SUPPORTED_SOCIAL_PLATFORMS.map((platform) => [
      platform,
      dbsSettings?.socialLinks?.find(({ type }) => type === platform)?.url ??
        '',
    ]),
  ) as Record<SupportedSocialPlatform, string>,
  tracking: Object.fromEntries(
    TRACKING_FIELDS.map((field) => [
      field,
      dbsSettings?.tracking?.[field] ?? '',
    ]),
  ) as Record<TrackingField, string>,
  widgetSettings: {
    requireGuestPhoneNumber:
      dbsSettings?.widgetSettings?.requireGuestPhoneNumber ?? false,
    hidePriceWhenDateNotEntered:
      dbsSettings?.widgetSettings?.hidePriceWhenDateNotEntered ?? false,
  },
});

export const getWebsiteSettingsSchema = (t: TFunction) => ({
  widgetCtaTitle: Yup.string()
    .trim()
    .max(
      WIDGET_CTA_TITLE_MAX_LENGTH,
      t('pageDirectBookingEngine.websiteSettings.widgetCtaTitleTooLong', {
        max: WIDGET_CTA_TITLE_MAX_LENGTH,
      }),
    ),
  tracking: Yup.object(
    Object.fromEntries(
      TRACKING_FIELDS.map((field) => [
        field,
        Yup.string().max(
          TRACKING_MAX_LENGTH,
          t('pageDirectBookingEngine.trackingSettings.idTooLong', {
            max: TRACKING_MAX_LENGTH,
          }),
        ),
      ]),
    ),
  ),
  featuredProperties: Yup.array(),
  socialLinks: Yup.object()
    .test(
      'socialUrlLength',
      t('pageDirectBookingEngine.websiteSettings.social.urlTooLong', {
        max: SOCIAL_URL_MAX_LENGTH,
      }),
      (value) =>
        SUPPORTED_SOCIAL_PLATFORMS.every(
          (platform) =>
            ((value as Record<string, string>)?.[platform] ?? '').trim()
              .length <= SOCIAL_URL_MAX_LENGTH,
        ),
    )
    .test(
      'socialUrlScheme',
      t('pageDirectBookingEngine.websiteSettings.social.urlInvalid'),
      (value) =>
        SUPPORTED_SOCIAL_PLATFORMS.every((platform) =>
          isSupportedSocialUrl(
            (value as Record<string, string>)?.[platform] ?? '',
          ),
        ),
    ),
});
